/**
 * App — xin.xyz Neo-Pixel 赛博 Agent 终端
 * 大框架：顶部 HUD + 左侧全息伴侣主舞台 + 右侧双模工作台 + 底部通联抽屉
 * 桌面(≥1024px) flex-row 双栏 / 移动(<768px) flex-col + 掌机 Tab
 */
import { Suspense, lazy, useCallback, useEffect, useState } from "react";
import { Zap, Backpack } from "lucide-react";
import StatusBar from "@/components/StatusBar";
import NeoPixelCard from "@/components/NeoPixelCard";
import ProductEntries from "@/components/ProductEntries";
import ErrorBoundary from "@/components/ErrorBoundary";
import BootLoader from "@/components/BootLoader";

/* 懒加载重模块：BootLoader 的 import() 会真实分包并推进进度 */
const VaultInventory = lazy(() => import("@/components/VaultInventory"));
const KernelHubTerminal = lazy(() => import("@/components/KernelHubTerminal"));
const GameShell = lazy(() => import("@/components/game/GameShell"));

import BuddyXin from "@/components/BuddyXin";
import BuddyBubble from "@/components/BuddyBubble";
import ContactDrawer, {
  KERNELHUB_STAMP_URL,
  ICLOSER_STAMP_URL,
} from "@/components/ContactDrawer";
import { audioEngine } from "@/lib/audioEngine";
import { bgmEngine } from "@/lib/bgmEngine";
import { useBuddyDialogue } from "@/lib/useBuddyDialogue";

type WorkbenchTab = "vault" | "kernelhub";

export function App() {
  const [tab, setTab] = useState<WorkbenchTab>("vault");
  const [seedInput] = useState("did:xin:default");
  /** BootLoader 门：false=加载中；true=资源就绪已直入游戏 */
  const [booted, setBooted] = useState(false);

  const dialogue = useBuddyDialogue();

  /* 全局首次手势唤醒 AudioContext：touchstart / click / keydown 任一触发 */
  useEffect(() => {
    const unlock = () => {
      void audioEngine.unlock();
      void bgmEngine.unlock();
    };
    const opts: AddEventListenerOptions = { once: true, passive: true };
    window.addEventListener("touchstart", unlock, opts);
    window.addEventListener("click", unlock, opts);
    window.addEventListener("keydown", unlock, opts);
    return () => {
      window.removeEventListener("touchstart", unlock);
      window.removeEventListener("click", unlock);
      window.removeEventListener("keydown", unlock);
    };
  }, []);

  /** 加载完成：直入游戏（极简改造：无分流），滚到游戏区 */
  const enterGame = useCallback(() => {
    setBooted(true);
    window.requestAnimationFrame(() => {
      document.getElementById("game-stage")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }, []);

  /** 通关一键入包：焦点工作台 + 钱柜 Tab（xin:enter-vault 由结算主 CTA 派发） */
  useEffect(() => {
    const onEnterVault = () => {
      setTab("vault");
      window.requestAnimationFrame(() => {
        document.getElementById("workbench-container")?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    };
    window.addEventListener("xin:enter-vault", onEnterVault);
    return () => window.removeEventListener("xin:enter-vault", onEnterVault);
  }, []);

  if (!booted) {
    return <BootLoader onReady={enterGame} />;
  }

  return (
    <div className="neo-grid-bg relative flex min-h-[100dvh] w-full flex-col overflow-x-hidden font-mono text-slate-100">
      {/* about.html 背景作为底层背景（极简改造）：固定全屏 / z-index 0 / 不吃指针 / 0.35 半透 */}
      <div
        aria-hidden="true"
        data-testid="about-backdrop"
        className="pointer-events-none fixed inset-0 opacity-[0.35]"
        style={{
          zIndex: 0,
          position: "fixed",
          inset: 0,
          pointerEvents: "none",
          opacity: 0.35,
          background:
            "linear-gradient(rgba(255, 255, 255, 0.06) 1px, transparent 1px), linear-gradient(90deg, rgba(255, 255, 255, 0.06) 1px, transparent 1px), radial-gradient(ellipse 60% 40% at 20% 0%, rgba(0, 240, 255, 0.08), transparent), radial-gradient(ellipse 50% 40% at 90% 10%, rgba(139, 92, 246, 0.1), transparent), #06080d",
          backgroundSize: "32px 32px, 32px 32px, auto, auto, auto",
        }}
      />
      {/* 全息微纹理 + 暗角（非交互覆盖层） */}
      <div className="crt-scanlines" aria-hidden="true" />
      <div className="crt-vignette" aria-hidden="true" />
      <div className="crt-glow" aria-hidden="true" />

      <ErrorBoundary label="HUD">
        <StatusBar />
      </ErrorBoundary>

      <main className="mx-auto w-full max-w-6xl flex-1 px-3 pt-6 pb-[calc(1.5rem+env(safe-area-inset-bottom))] sm:px-5">
        {/* ── 工单 7 主舞台：赛博中继突围战（可玩 Canvas RPG） ── */}
        <ErrorBoundary label="GAME">
          <Suspense fallback={<div className="h-[100dvh] w-full bg-[#050811]" />}>
            <GameShell />
          </Suspense>
        </ErrorBoundary>

        {/* ── 伴侣与工作台：桌面双栏 / 移动堆叠（前序工单文案与资产原样保留） ── */}
        <div className="flex flex-col gap-5 lg:grid lg:grid-cols-[minmax(0,44%)_minmax(0,56%)] lg:items-stretch lg:gap-4">
          {/* 左栏 ~40%：全息伴侣主舞台 */}
          <aside id="companion-stage" className="relative w-full">
            <div className="neo-glow neo-glow-cyan" aria-hidden="true" />
            <ErrorBoundary label="COMPANION">
              <NeoPixelCard
                accent="cyan"
                className="relative z-[1] flex min-h-[180px] flex-col items-center justify-center px-3 py-5 lg:h-full lg:min-h-[560px]"
              >
                <div className="mb-2 font-pixel text-[8px] tracking-[0.25em] text-[#00f0ff]/80">
                  COMPANION STAGE // 全息灵机
                </div>

                {/* 对话气泡 + 伴侣挂载（工单 2/5） */}
                <div className="relative flex w-full justify-center">
                  <BuddyBubble
                    line={dialogue.line}
                    anchor={{ x: 160, y: 8 }}
                    onCta={() => audioEngine.play("coin")}
                  />
                  <BuddyXin
                    seedInput={seedInput}
                    anim={dialogue.anim}
                    onPoke={dialogue.onPoke}
                  />
                </div>

                <div className="mt-3 border border-[#00f0ff]/20 bg-[#06080d]/80 px-3 py-1.5 font-pixel text-[7px] tracking-widest text-[#8aa3c2]">
                  [ SYSTEM STANDBY // 全息灵机“信”神经节点待接入... ]
                </div>
              </NeoPixelCard>
            </ErrorBoundary>
          </aside>

          {/* 右栏 ~60%：双模工作台（悬停语义：神枢/钱柜分区触发） */}
          <section
            id="workbench-container"
            className="relative flex w-full min-w-0 flex-col"
            onMouseLeave={() => {
              dialogue.onLeaveKernelHub();
              dialogue.onLeaveVault();
            }}
          >
            <div className="neo-glow neo-glow-violet" aria-hidden="true" />

            {/* 掌机按键式双选项卡：单手点按即时切换 */}
            <div
              className="relative z-[1] mb-3 flex flex-wrap items-center justify-center gap-2 sm:justify-start"
              role="tablist"
              aria-label="工作台切换"
            >
              <button
                type="button"
                role="tab"
                aria-selected={tab === "kernelhub"}
                className="neo-tab"
                data-active={tab === "kernelhub"}
                onMouseEnter={() => {
                  audioEngine.play("beep");
                  dialogue.onHoverKernelHub();
                }}
                onClick={() => {
                  setTab("kernelhub");
                  audioEngine.play("select");
                  dialogue.onHoverKernelHub();
                }}
              >
                <Zap className="h-4 w-4" strokeWidth={2.5} aria-hidden />
                [ ⚡ KERNELHUB // 神枢 ]
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={tab === "vault"}
                className="neo-tab"
                data-active={tab === "vault"}
                onMouseEnter={() => {
                  audioEngine.play("beep");
                  dialogue.onHoverVault();
                }}
                onClick={() => {
                  setTab("vault");
                  audioEngine.play("select");
                  dialogue.onHoverVault();
                }}
              >
                <Backpack className="h-4 w-4" strokeWidth={2.5} aria-hidden />
                [ 🎒 ICLOSER VAULT // 钱柜 ]
              </button>
            </div>

            <ErrorBoundary label="WORKBENCH">
              <NeoPixelCard
                accent="violet"
                className="relative z-[1] p-3 sm:p-4"
                hoverable={false}
              >
                {/* 双组件保状态挂载：非激活面板渲染到屏幕外（保 innerText/textContent 可逐字比对，状态不丢失） */}
                <div
                  id="vault-slot"
                  className={
                    tab === "vault"
                      ? "relative h-full w-full"
                      : "pointer-events-none absolute left-[-9999px] top-0 w-[960px] overflow-hidden opacity-0"
                  }
                >
                  <ErrorBoundary label="VAULT">
                    <Suspense fallback={<div className="h-32 w-full" />}>
                      <VaultInventory />
                    </Suspense>
                  </ErrorBoundary>
                </div>
                <div
                  id="kernelhub-slot"
                  className={
                    tab === "kernelhub"
                      ? "relative h-full w-full"
                      : "pointer-events-none absolute left-[-9999px] top-0 w-[960px] overflow-hidden opacity-0"
                  }
                >
                  <ErrorBoundary label="TERMINAL">
                    <Suspense fallback={<div className="h-32 w-full" />}>
                      <KernelHubTerminal />
                    </Suspense>
                  </ErrorBoundary>
                </div>
              </NeoPixelCard>
            </ErrorBoundary>

            {/* 预留：纵向分栏并存结构（工单 1.5 插槽契约） */}
            <div
              id="workbench-stack-slot"
              className="mt-3 hidden"
              aria-hidden="true"
            />

            {/* KernelHub ↔ iCloser 关系标识（工单：以更克制的系统标识表达，不再做漂浮大标签；
                保留原链接与音效交互，仅为布局/信息层级重排） */}
            <nav
              aria-label="KernelHub 与 iCloser 基础设施关系"
              className="relative z-[1] mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 sm:gap-x-3 lg:mt-auto"
            >
              <a
                href={KERNELHUB_STAMP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="contact-rel"
                data-node="kernelhub"
                onMouseEnter={() => audioEngine.play("beep")}
                onClick={() => audioEngine.play("select")}
              >
                <span className="contact-rel-led" aria-hidden="true" />
                <span className="font-pixel text-[8px] tracking-[0.16em] text-[#38bdf8]">
                  KERNELHUB
                </span>
                <span className="font-mono text-[10px] text-[#8aa3c2]">
                  // CONTROL PLANE
                </span>
              </a>
              <span
                className="contact-rel-link font-mono text-[11px] text-[#8aa3c2]"
                aria-hidden="true"
              >
                →
              </span>
              <a
                href={ICLOSER_STAMP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="contact-rel"
                data-node="icloser"
                onMouseEnter={() => audioEngine.play("beep")}
                onClick={() => audioEngine.play("select")}
              >
                <span className="contact-rel-led" aria-hidden="true" />
                <span className="font-pixel text-[8px] tracking-[0.16em] text-[#fbbf24]">
                  ICLOSER
                </span>
                <span className="font-mono text-[10px] text-[#8aa3c2]">
                  // WALLET INFRASTRUCTURE
                </span>
              </a>
            </nav>
          </section>
        </div>

        {/* ── 技术产品并列入口（工单：RPC Manager / DevOmni）—— 复用现有视觉系统，不改 Hero ── */}
        <ErrorBoundary label="PRODUCTS">
          <ProductEntries />
        </ErrorBoundary>

        {/* 底部通联抽屉（工单 6）+ 全面屏安全区 */}
        <div id="contact-drawer-slot" className="mt-8 neo-safe-bottom">
          <ErrorBoundary label="CONTACT">
            <ContactDrawer />
          </ErrorBoundary>
        </div>
      </main>
    </div>
  );
}

export default App;
